import { createTheme } from '@mui/material/styles';

interface WhiteLabelSettings {
  appName: string;
  appLogoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  enableCustomDomain: boolean;
  customDomain: string;
}

export function createAppTheme(mode: 'light' | 'dark', whiteLabelSettings: WhiteLabelSettings | null) {
  function hexToRgb(hex: string) {
    const h = hex.replace('#', '');
    const bigint = parseInt(h.length === 3 ? h.split('').map(c=>c+c).join('') : h, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
  }
  function luminance(hex: string) {
    const { r, g, b } = hexToRgb(hex);
    const srgb = [r, g, b].map(v => v / 255).map(v => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  }
  function contrastRatio(hex1: string, hex2: string) {
    const l1 = luminance(hex1);
    const l2 = luminance(hex2);
    const top = Math.max(l1, l2) + 0.05;
    const bottom = Math.min(l1, l2) + 0.05;
    return +(top / bottom).toFixed(2);
  }
  // Ensure primary color meets contrast against white (4.5:1). If not, fallback to darker brand.
  const requestedPrimary = (whiteLabelSettings?.primaryColor || '#2563EB').toString();
  const safePrimary = contrastRatio(requestedPrimary, '#ffffff') >= 4.5 ? requestedPrimary : '#1e40af';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: safePrimary,
        light: '#60A5FA',
        dark: '#1e40af',
        contrastText: '#ffffff',
      },
      secondary: {
        main: whiteLabelSettings?.secondaryColor || '#8B5CF6',
        light: '#A78BFA',
        dark: '#7C3AED',
        contrastText: '#ffffff',
      },
      background: {
        default: mode === 'dark' ? '#121212' : '#fff',
        paper: mode === 'dark' ? '#1d1d1d' : '#fff',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: safePrimary,
            color: '#fff',
            '&:hover': {
              backgroundColor: '#1e40af',
            },
          },
        },
      },
    },
    typography: {
      fontFamily: 'Inter, Arial, sans-serif',
      h1: {
        fontSize: '2.5rem',
        '@media (min-width:600px)': {
          fontSize: '3rem',
        },
        '@media (min-width:960px)': {
          fontSize: '4rem',
        },
      },
      h2: {
        fontSize: '2rem',
        '@media (min-width:600px)': {
          fontSize: '2.5rem',
        },
        '@media (min-width:960px)': {
          fontSize: '3.5rem',
        },
      },
      h4: {
        fontSize: '1.5rem',
        '@media (min-width:600px)': {
          fontSize: '1.8rem',
        },
        '@media (min-width:960px)': {
          fontSize: '2.2rem',
        },
      },
      h6: {
        fontSize: '1rem',
        '@media (min-width:600px)': {
          fontSize: '1.1rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.25rem',
        },
      },
      body1: {
        fontSize: '0.875rem',
        '@media (min-width:600px)': {
          fontSize: '1rem',
        },
        '@media (min-width:960px)': {
          fontSize: '1.125rem',
        },
      },
    },
  });
}
